import React, { useMemo } from "react";
import { ChevronBottomIcon, ChevronTopIcon, Modal } from "@pd/pd-ui-kit";
import { Button, Table, TableProps } from "antd";
import { useTranslation } from "react-i18next";

import {
  CombinationRulesPrecheckResponse,
  ICombinationRulePrecheckRecord,
} from "~/entity/CombinationRules";

import s from "./CheckAllPreviewModal.less";

type PrecheckRow = ICombinationRulePrecheckRecord & { key: string };

type Props = {
  open: boolean;
  result?: CombinationRulesPrecheckResponse;
  loading: boolean;
  confirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const CheckAllPreviewModal = ({
  open,
  result,
  loading,
  confirming,
  onCancel,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();

  const columns: TableProps<PrecheckRow>["columns"] = [
    {
      title: t("combinationRules.precheck_dealer"),
      dataIndex: "dealerName",
      key: "dealerName",
      width: 220,
    },
    {
      title: t("combinationRules.precheck_employee"),
      dataIndex: "employeeFio",
      key: "employeeFio",
      width: 220,
    },
    {
      title: t("combinationRules.precheck_position"),
      dataIndex: "position",
      key: "position",
      width: 200,
    },
    Table.EXPAND_COLUMN,
  ];

  const dataSource = useMemo(
    () =>
      (result?.dealers ?? []).reduce<PrecheckRow[]>((rows, dealer) => {
        dealer.records.forEach((record, recordIndex) => {
          rows.push({
            ...record,
            dealerName: record.dealerName || dealer.dealerName,
            key: `${dealer.dealerId}-${recordIndex}`,
          });
        });
        return rows;
      }, []),
    [result],
  );

  return (
    <Modal
      open={open}
      title={t("combinationRules.precheck_title")}
      onCancel={onCancel}
      centered
      size="large"
      destroyOnHidden
      cancelButton={{ children: t("ui.cancel"), disabled: confirming }}
      okButton={{
        children: t("ui.confirm"),
        loading: confirming,
        disabled: loading,
        onClick: onConfirm,
      }}
    >
      <Table<PrecheckRow>
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        rowKey="key"
        size="small"
        pagination={false}
        expandable={{
          rowExpandable: (record) => Boolean(record.message),
          expandedRowClassName: () => s.expandedRow,
          expandedRowRender: (record, _index, _indent, expanded) => (
            <div
              className={`${s.reasonAccordion} ${
                expanded ? s.reasonAccordionExpanded : ""
              }`}
              aria-hidden={!expanded}
            >
              <div className={s.reasonContent}>
                <div className={s.reasonText}>
                  <strong>{t("combinationRules.precheck_reason")}:</strong>{" "}
                  {record.message}
                </div>
              </div>
            </div>
          ),
          expandIcon: ({ expandable, expanded, onExpand, record }) =>
            expandable ? (
              <Button
                type="text"
                size="small"
                aria-label={t(
                  expanded
                    ? "combinationRules.precheck_hide_reason"
                    : "combinationRules.precheck_show_reason",
                )}
                aria-expanded={expanded}
                icon={
                  expanded ? (
                    <ChevronTopIcon size={16} />
                  ) : (
                    <ChevronBottomIcon size={16} />
                  )
                }
                onClick={(event) => onExpand(record, event)}
              />
            ) : null,
        }}
        locale={{ emptyText: t("combinationRules.precheck_empty") }}
      />
    </Modal>
  );
};

export default CheckAllPreviewModal;
